import { useState, Suspense } from 'react';
import { useLocation, useNavigate, useSubmit, useLoaderData } from 'react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';
import Page from 'client/components/aria/Page/Page';
import Alert from 'client/components/aria/Alert/Alert';
import Button from 'client/components/aria/Button/Button';
import BackLink from 'client/components/aria/BackLink/BackLink';
import LoadingScreen from 'client/components/suc/LoadingScreen/LoadingScreen';
import EnquiryAlertSuccess from 'client/pages/EnquiriesPage/components/EnquiryAlerts/EnquiryAlertSuccess/EnquiryAlertSuccess';
import EnquirySummary from 'client/pages/EnquiriesPage/ViewEnquiryPage/EnquirySummary/EnquirySummary';
import EnquiryDetails from 'client/pages/EnquiriesPage/components/EnquiryDetails/EnquiryDetails';
import EnquiryPageTitle from 'client/pages/EnquiriesPage/components/EnquiryPageTitle/EnquiryPageTitle';
import Link from 'client/components/aria/Link/Link';
import SummaryItem from 'client/components/ui/SummaryItem/SummaryItem';
import { ENQUIRIES_PAGE, CREATE_WORKPACK_PAGE, MAP_PAGE } from 'client/utils/routes/routeConstants';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import WarningText from 'client/components/aria/WarningText/WarningText';
import NotificationContactDetailsModal from '../components/NotificationContactDetailsModal/NotificationContactDetailsModal';
import AffectedAssetOwnersNotification from 'client/pages/EnquiriesPage/components/AffectedAssetOwnersNotification/AffectedAssetOwnersNotification';
import NotificationStatuses from 'client/pages/EnquiriesPage/components/NotificationStatuses/NotificationStatuses';
import EmergencyContactsList from '../components/EmergencyContactsList/EmergencyContactsList';

const createEmptyContactModalState = () => ({
    isOpen: false,
    organisationName: '',
    contacted: undefined,
    contacts: {
        business: [],
        emergency: []
    }
});

const ViewEnquiryPage = () => {
    const enquiryData = useLoaderData();
    const intl = useIntl();
    const { state } = useLocation();
    const [contactModalState, setContactModalState] = useState(createEmptyContactModalState);
    const navigate = useNavigate();
    const submit = useSubmit();

    /**
     * Modal state management for displaying contact details
     * The contacts are passed from child components (AffectedAssetOwnersNotification, NotificationStatuses)
     * which have already have the data from multiple sources
     */
    const closeContactModal = () => {
        setContactModalState(createEmptyContactModalState());
    };

    const openContactModal = ({ organisationName, contacts, contacted }) => {
        setContactModalState({
            isOpen: true,
            organisationName,
            contacts,
            contacted
        });
    };

    const handleBackLink = () => {
        navigate(-1);
    };

    const renderEnquiryPageContent = () => {
        const {
            enquiry,
            activityTypes,
            assetOwners, // Contains contact details from AssetOwnerService (operationalZone & assetsInAOIOwners)
            sortedAoEnhancedMeasures = [],
            affectedAssetOwners = [], // List of affected organisations (without contact details)
            notificationStatuses = [] // List of notified organisations with their notification status
        } = enquiryData;
        const isJustCreated = Boolean(state?.justCreated);
        const isEmergency = Boolean(enquiry?.isEmergency ?? enquiry?.emergency);
        const enquiryName = enquiry?.title ?? 'N/A';
        const referenceText = enquiry?.jobReference ? ` - ${enquiry.jobReference}` : '';
        const mapHref = state?.mapHref || (enquiry?.enquiryId ? `${MAP_PAGE}?enquiry=${enquiry.enquiryId}` : MAP_PAGE);

        const startWorkpackFlow = () => {
            if (!enquiry?.geometry) {
                navigate(CREATE_WORKPACK_PAGE);
                return;
            }

            const formData = new FormData();
            formData.append('draft', 'true');
            formData.append('geometry', JSON.stringify(enquiry.geometry));

            const appendIfString = (key, value) => {
                if (typeof value === 'string' && value.trim().length > 0) {
                    formData.append(key, value);
                }
            };

            appendIfString('title', enquiry.title);
            appendIfString('description', enquiry.description);
            appendIfString('jobReference', enquiry.jobReference);

            if (enquiry?.purpose) {
                formData.append('purpose', enquiry.purpose);
            }

            const emergencyFlag = enquiry?.isEmergency ?? enquiry?.emergency;
            if (typeof emergencyFlag === 'boolean') {
                formData.append('isEmergency', emergencyFlag ? 'true' : 'false');
            }

            const orientation = enquiry?.workpackOrientation;
            if (typeof orientation === 'string' && orientation.trim().length > 0) {
                formData.append('workpackOrientation', orientation);
            }

            const scale = enquiry?.workpackScale;
            if (scale !== undefined && scale !== null) {
                formData.append('workpackScale', String(scale));
            }

            submit(formData, {
                action: CREATE_WORKPACK_PAGE,
                method: 'put'
            });
        };

        const renderViewHeader = () => (
            <>
                <div className="govuk-grid-row govuk-!-margin-top-4">
                    <div className="govuk-grid-column-full">
                        <BackLink onClick={handleBackLink} data-testid="viewEnquiryPage.backLink">
                            {intl.formatMessage({ id: 'general.back' })}
                        </BackLink>
                        <EnquiryPageTitle>
                            {enquiry.title}
                        </EnquiryPageTitle>
                        <p className='govuk-body govuk-!-margin-top-6'>
                            <strong>{intl.formatMessage({ id: 'viewEnquiryPage.subtitle' })}</strong>
                        </p>
                        <p className="govuk-body govuk-!-margin-top-2">
                            {intl.formatMessage({ id: 'viewEnquiryPage.description' })}
                        </p>
                    </div>
                </div>
            </>
        );

        const renderConfirmationHeader = () => (
            <>
                <div className="govuk-grid-row govuk-!-margin-top-4">
                    <div className="govuk-grid-column-full">
                        <EnquiryAlertSuccess enquiryId={enquiry?.enquiryId ?? enquiry?.id} />
                    </div>
                </div>
                <div className="govuk-grid-row govuk-!-margin-top-4">
                    <div className="govuk-grid-column-full">
                        <p className="govuk-body">
                            <FormattedMessage
                                id="viewEnquiryPage.confirmation.message"
                                values={{
                                    enquiryName,
                                    referenceText,
                                    Link: text => <Link href={ENQUIRIES_PAGE}>{text}</Link>,
                                    strong: chunks => <strong>{chunks}</strong>
                                }}
                            />
                        </p>
                        <p className="govuk-body">
                            {intl.formatMessage({ id: 'viewEnquiryPage.workpack.info' })}
                        </p>
                    </div>
                </div>
                <div className="govuk-grid-row govuk-!-margin-top-4">
                    <div className="govuk-grid-column-full">
                        <Button
                            onPress={startWorkpackFlow}
                            variant="success"
                            data-testid='makeWorkpackButton'
                            className="govuk-!-margin-bottom-0 test"
                        >
                            {intl.formatMessage({ id: 'viewEnquiryPage.makeWorkpack' })}
                        </Button>
                    </div>
                </div>
            </>
        );

        const renderEnhancedMeasuresSection = () => {
            if (!sortedAoEnhancedMeasures.length) {
                return null;
            }

            return (
                <>
                    {/* Only show this sensitive sites section if assets with enhanced measures are included in the AOI */}
                    <div className="govuk-grid-row govuk-!-margin-top-8">
                        <div className="govuk-grid-column-full">
                            <EnquiryDetails>
                                <FormattedMessage id='viewEnquiryPage.enhancedMeasures.title' />
                                <div className='govuk-!-margin-top-6'>
                                    <Alert
                                        title={intl.formatMessage({ id: 'viewEnquiryPage.enhancedMeasures.alert.title' })}
                                        variant="secondary"
                                    >
                                        <p className='govuk-body'>
                                            <strong>
                                                <WarningText message={intl.formatMessage({ id: 'viewEnquiryPage.enhancedMeasures.alert.intro' })} />
                                            </strong>
                                        </p>
                                        <div className="govuk-body">
                                            {!isEmergency && sortedAoEnhancedMeasures.map((measure, index) => (
                                                <p key={measure.displayName ?? index}><strong>{measure.displayName}</strong></p>
                                            ))}
                                            {isEmergency && (
                                                <EmergencyContactsList
                                                    sortedAoEnhancedMeasures={sortedAoEnhancedMeasures}
                                                    assetOwners={assetOwners}
                                                />
                                            )}
                                        </div>
                                    </Alert>
                                </div>
                            </EnquiryDetails>
                        </div>
                    </div>
                </>
            );
        };

        const renderSummarySection = () => (
            <>
                <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full govuk-!-margin-top-4">
                        <EnquiryDetails>
                            {intl.formatMessage({ id: 'viewEnquiryPage.details' })}
                        </EnquiryDetails>

                        <SummaryItem
                            title={intl.formatMessage({ id: 'viewEnquiryPage.summary.dateCreated' })}
                            data-testid="enquiry-view-dateCreated"
                        >
                            {moment(enquiry.dateCreated).format('D MMM YYYY, h:mma')}
                        </SummaryItem>
                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />

                        <SummaryItem
                            title={intl.formatMessage({ id: 'viewEnquiryPage.summary.workpack' })}
                            data-testid="enquiry-view-workpack"
                        >
                            <Button
                                onPress={startWorkpackFlow}
                                data-testid="viewEnquiryPage.workpackButton"
                                className="govuk-!-margin-bottom-0"
                            >
                                {intl.formatMessage({ id: 'viewEnquiryPage.workpack' })}
                            </Button>
                            <p className="govuk-body govuk-!-margin-bottom-0 govuk-!-margin-top-2">
                                {intl.formatMessage({ id: 'viewEnquiryPage.workpack.description' })}
                            </p>
                        </SummaryItem>
                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />

                    </div>
                </div>
                <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full">
                        <EnquirySummary
                            enquiry={enquiry}
                            activityTypes={activityTypes}
                            assetOwners={assetOwners?.assetsInAOIOwners}
                        />
                    </div>
                </div>
            </>
        );

        const renderAffectedAssetOwnerNotificationSection = () => {
            if (!affectedAssetOwners.length) {
                return null;
            }

            /**
             * This section displays organisations affected by the enquiry and allows viewing their contact details
             *
             * Data flow:
             * 1. affectedAssetOwners: List from EnquiryRequestService.getAffectedAssetOwners (no contacts)
             * 2. assetOwners: Contains contact details from AssetOwnerService.getAssetOwners
             * 3. AffectedAssetOwnersNotification: Reconciles both sources to match contacts with organisations
             * 4. onShowContactDetails: Callback that receives the matched contacts and displays them in a modal
             */
            return (
                <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full govuk-!-margin-top-8">
                        <EnquiryDetails>
                            <FormattedMessage id='viewEnquiryPage.notification.title' />
                            <p className="govuk-hint">
                                <FormattedMessage id='viewEnquiryPage.notification.hint'/>
                            </p>
                            <div className="govuk-!-margin-top-4">
                                <AffectedAssetOwnersNotification
                                    affectedAssetOwners={affectedAssetOwners}
                                    assetOwners={assetOwners}
                                    isEmergency={isEmergency}
                                    onShowContactDetails={openContactModal}
                                />
                            </div>
                        </EnquiryDetails>
                        <div className="govuk-!-margin-top-8 govuk-!-margin-bottom-10">
                            <EnquiryDetails>{intl.formatMessage({ id: 'viewEnquiryPage.nextSteps.title' })}</EnquiryDetails>
                            <p className="govuk-body">
                                {intl.formatMessage({ id: 'viewEnquiryPage.nextSteps.description' })}
                            </p>
                            <p className='govuk-body'>
                                <FormattedMessage
                                    id='viewEnquiryPage.nextSteps.backToMap'
                                    values={{
                                        Link: chunks => <Link href={mapHref}>{chunks}</Link>
                                    }}
                                />
                            </p>
                        </div>
                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                    </div>
                </div>
            );
        };

        const renderNotificationStatusesSection = () => {
            if (!notificationStatuses.length) {
                return null;
            }

            return (
                <div className="govuk-grid-row">
                    <div className="govuk-grid-column-full govuk-!-margin-top-8">
                        <EnquiryDetails>
                            <FormattedMessage id='viewEnquiryPage.notificationStatuses.title' />
                            <div className="govuk-!-margin-top-4">
                                <NotificationStatuses
                                    notificationStatuses={notificationStatuses}
                                    assetOwners={assetOwners}
                                    onShowContactDetails={openContactModal}
                                />
                            </div>
                        </EnquiryDetails>
                        <div className="govuk-!-margin-top-8 govuk-!-margin-bottom-6">
                            <EnquiryDetails>{intl.formatMessage({ id: 'viewEnquiryPage.nextSteps.title' })}</EnquiryDetails>
                            <p className="govuk-body">
                                {intl.formatMessage({ id: 'viewEnquiryPage.nextSteps.description' })}
                            </p>
                            <p className='govuk-body'>
                                <FormattedMessage
                                    id='viewEnquiryPage.nextSteps.backToMap'
                                    values={{
                                        Link: chunks => <Link href={mapHref}>{chunks}</Link>
                                    }}
                                />
                            </p>
                        </div>
                        <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                    </div>
                </div>
            );
        };

        return (
            <>
                {isJustCreated ? renderConfirmationHeader() : renderViewHeader()}
                {renderEnhancedMeasuresSection()}
                {isJustCreated ? renderAffectedAssetOwnerNotificationSection() : renderSummarySection()}
                {!isJustCreated && renderNotificationStatusesSection()}
                <NotificationContactDetailsModal
                    isOpen={contactModalState.isOpen}
                    onClose={closeContactModal}
                    organisationName={contactModalState.organisationName}
                    contacts={contactModalState.contacts}
                    contacted={contactModalState.contacted}
                    showEmergencyContacts={isEmergency}
                />
            </>
        );
    };

    return (
        <Page title={intl.formatMessage({ id: 'viewEnquiryPage.title' })} data-testid="viewEnquiryPage">
            <Suspense fallback={<LoadingScreen />}>
                {renderEnquiryPageContent()}
            </Suspense>
        </Page>
    );
};

ViewEnquiryPage.propTypes = {
    ...defaultAriaProps
};

export default ViewEnquiryPage;
